/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useCallback, useState} from 'react';
import {useAgentsAPI} from './useAgentsAPI';
import {APIParams, METHODS} from '../../../hooks/abstractions/useTrusstedAgent';
import {AGENTS_API_EVENT_NAMES} from '../../../types/agents-api';

// Define the new phone number item structure
export interface PhoneNumberItem {
  agentId: string | null;
  phoneNumber: string;
  agentName?: string | null;
}

export interface PhoneNumbersResponse {
  phoneNumbers: PhoneNumberItem[];
}

export const useGetAgentPhoneNumbers = () => {
  const agentsAPI = useAgentsAPI();
  // Add a new state for the full phone number items
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumberItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result: PhoneNumbersResponse = await agentsAPI({
        eventName: AGENTS_API_EVENT_NAMES.ListPhoneNumbers,
        methodName: METHODS.GET,
        params: {},
        queryParams: {},
      } as APIParams);

      // Store the full phone number items
      setPhoneNumbers(result.phoneNumbers || []);
      setIsLoading(false);
    } catch (err) {
      setError('Error fetching phone numbers');
      setIsLoading(false);
    }
  }, [agentsAPI]);

  return {
    phoneNumbers,
    isLoading,
    error,
    fetch,
  };
};
